import React, { useState, useMemo, useContext, createContext } from 'react';
import { TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import {
  IconButton,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';

// Create a context to share rows and filter states stably without causing remounts
export const HeaderFilterContext = createContext({
  rows: [],
  columnFilters: {},
  onFilterChange: () => {}
});

export const getRowCellValue = (row, columnName) => {
  if (columnName === 'customerStatus') {
    return row.customerStatus || '(Blank)';
  }

  const val = row[columnName];
  if (val === undefined || val === null || val === '') {
    return '(Blank)';
  }
  return String(val);
};

const HeaderFilterCell = ({ column, children, ...restProps }) => {
  const { rows, columnFilters, onFilterChange } = useContext(HeaderFilterContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Get unique values for this column
  const uniqueValues = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const values = rows.map((row) => getRowCellValue(row, column.name));
    // eslint-disable-next-line no-undef
    return Array.from(new Set(values)).sort((a, b) => {
      if (a === '(Blank)') return 1;
      if (b === '(Blank)') return -1;
      return a.localeCompare(b);
    });
  }, [rows, column.name]);

  const activeFilters = columnFilters[column.name] || [];
  const isFiltered = activeFilters.length > 0;

  // Local state for checkboxes while the popover is open
  const [tempSelected, setTempSelected] = useState([]);

  const handleOpen = (event) => {
    event.stopPropagation(); // Prevent sorting trigger
    // Start with the applied values. An unfiltered column starts empty so a
    // user can search and select a result without first deselecting everything.
    const currentActive = columnFilters[column.name];
    setTempSelected(currentActive && currentActive.length > 0 ? currentActive : []);
    setSearchTerm('');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleValue = (value) => {
    setTempSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleToggleSelectAll = () => {
    const allVisibleSelected = filteredValues.every((value) => tempSelected.includes(value));
    if (allVisibleSelected) {
      setTempSelected((previous) => previous.filter((value) => !filteredValues.includes(value)));
    } else {
      // eslint-disable-next-line no-undef
      setTempSelected((previous) => Array.from(new Set([...previous, ...filteredValues])));
    }
  };

  const handleApply = () => {
    // If all items are selected, it's equivalent to no filter
    if (tempSelected.length === uniqueValues.length || tempSelected.length === 0) {
      const newFilters = { ...columnFilters };
      delete newFilters[column.name];
      onFilterChange(newFilters);
    } else {
      onFilterChange({
        ...columnFilters,
        [column.name]: tempSelected
      });
    }
    handleClose();
  };

  const handleClear = () => {
    const newFilters = { ...columnFilters };
    delete newFilters[column.name];
    onFilterChange(newFilters);
    handleClose();
  };

  // Do not show filter icon for columns with no data/single values
  const showFilterIcon = uniqueValues.length > 1;

  // filtered list based on search
  const filteredValues = useMemo(() => {
    if (!searchTerm.trim()) return uniqueValues;
    const term = searchTerm.toLowerCase();
    return uniqueValues.filter((val) => val.toLowerCase().includes(term));
  }, [uniqueValues, searchTerm]);

  return (
    <TableHeaderRow.Cell column={column} {...restProps}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
        {showFilterIcon && (
          <IconButton
            size="small"
            onClick={handleOpen}
            onMouseDown={(e) => e.stopPropagation()} // Prevent column sorting on mouse down
            sx={{
              padding: '2px',
              color: isFiltered ? 'primary.main' : 'action.active',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <FilterAltIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        )}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onMouseDown={(e) => e.stopPropagation()} // Prevent grid from intercepting clicks
        onClick={(e) => e.stopPropagation()} // Prevent sorting triggers
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            padding: 1,
            width: 250,
            maxHeight: 400,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()} // don't let grid intercept keystrokes
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            autoFocus
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={filteredValues.length > 0 && filteredValues.every((v) => tempSelected.includes(v))}
                indeterminate={
                  filteredValues.some((v) => tempSelected.includes(v)) &&
                  !filteredValues.every((v) => tempSelected.includes(v))
                }
                onChange={handleToggleSelectAll}
                size="small"
              />
            }
            label={
              searchTerm ? `Select all ${filteredValues.length} matching` : `Select all ${filteredValues.length} values`
            }
          />
          <Box sx={{ px: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>{tempSelected.length} selected</Box>
          <Divider />
          <Box sx={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', pl: 1 }}>
            <FormGroup>
              {filteredValues.length === 0 ? (
                <Box sx={{ p: 1, color: 'text.secondary', fontSize: '0.875rem' }}>No matches</Box>
              ) : (
                filteredValues.map((val) => (
                  <FormControlLabel
                    key={val}
                    control={
                      <Checkbox
                        checked={tempSelected.includes(val)}
                        onChange={() => handleToggleValue(val)}
                        size="small"
                      />
                    }
                    label={val}
                  />
                ))
              )}
            </FormGroup>
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
            <Button size="small" onClick={handleClear} disabled={!isFiltered}>
              Clear
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleApply}>
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </TableHeaderRow.Cell>
  );
};

export default HeaderFilterCell;

HeaderFilterCell.propTypes = {
  column: PropTypes.any,
  children: PropTypes.node
};
